import { FACET_TITLES, facetLabel, SOURCE_LABELS, TIER_LABELS } from "@/components/Backgrounds/facetLabels";
import type { CoachOption } from "@/containers/Coach/PromptStudio/components/CoachPicker";
import {
  BACKGROUND_FACETS,
  BackgroundFacet,
  BackgroundRatio,
  BackgroundSearchBody,
  BackgroundSource,
  BackgroundTier,
  BackgroundVisibility,
} from "@/services/bedrock/dtos/BackgroundAsset";

/** Admin curation, or a coach's own library (no curation controls). */
export type LibraryMode = "admin" | "coach";
export type LibraryScope = "all" | "global" | "coach";
export type LibraryFlag = "hasPeople" | "hasText" | "hasLogo";
export type PreviewRatio = Extract<BackgroundRatio, "square_1_1" | "portrait_4_5" | "portrait_9_16">;

export interface LibraryFilters {
  q: string;
  facets: Partial<Record<BackgroundFacet, string[]>>;
  visibility: BackgroundVisibility[];
  source: BackgroundSource[];
  tier: BackgroundTier[];
  /** true asks for assets with the thing, false for assets without it; unset doesn't filter. */
  flags: Partial<Record<LibraryFlag, boolean>>;
  sort: "recent" | "popular";
  /** The size tiles show; resolves each row's picture for it and never filters. */
  previewRatio: PreviewRatio;
}

export const DEFAULT_LIBRARY_FILTERS: LibraryFilters = {
  q: "",
  facets: {},
  visibility: ["draft", "active"],
  source: [],
  tier: [],
  flags: {},
  sort: "recent",
  previewRatio: "portrait_4_5",
};

/** A coach sees active backgrounds until they ask for their archived ones too. */
export const DEFAULT_COACH_FILTERS: LibraryFilters = { ...DEFAULT_LIBRARY_FILTERS, visibility: ["active"] };

export const defaultFiltersFor = (mode: LibraryMode) =>
  mode === "coach" ? DEFAULT_COACH_FILTERS : DEFAULT_LIBRARY_FILTERS;

export const FACETS = Object.keys(BACKGROUND_FACETS) as BackgroundFacet[];
export const FLAGS: LibraryFlag[] = ["hasPeople", "hasText", "hasLogo"];

const LIBRARY_PAGE_SIZE = 50;

/** The search the grid runs for these filters. */
export function librarySearchBody(
  filters: LibraryFilters,
  scope: LibraryScope,
  coach: CoachOption | null
): BackgroundSearchBody {
  const body: BackgroundSearchBody = {
    scope,
    sort: filters.sort,
    perPage: LIBRARY_PAGE_SIZE,
    previewRatio: filters.previewRatio,
  };
  if (scope === "coach" && coach) body.coachId = coach.id;
  if (filters.q) body.q = filters.q;
  FACETS.forEach(facet => {
    const values = filters.facets[facet];
    if (values && values.length > 0) body[facet] = values;
  });
  if (filters.visibility.length > 0) body.visibility = filters.visibility;
  if (filters.source.length > 0) body.source = filters.source;
  if (filters.tier.length > 0) body.tier = filters.tier;
  FLAGS.forEach(flag => {
    const value = filters.flags[flag];
    if (value !== undefined) body[flag] = value;
  });
  return body;
}

const sameValues = (a: string[], b: string[]) => a.length === b.length && a.every(value => b.includes(value));

/** What the Filters badge counts: each facet in use, each flag, and each admin curation filter off its default. */
export function activeFilterCount(filters: LibraryFilters, mode: LibraryMode): number {
  let count = FACETS.filter(facet => (filters.facets[facet]?.length ?? 0) > 0).length;
  count += FLAGS.filter(flag => filters.flags[flag] !== undefined).length;
  if (mode === "admin") {
    if (!sameValues(filters.visibility, DEFAULT_LIBRARY_FILTERS.visibility)) count += 1;
    if (filters.source.length > 0) count += 1;
    if (filters.tier.length > 0) count += 1;
  }
  return count;
}

/** The popover's filters back to their defaults; search, scope, sort, and preview stay. */
export function clearPopoverFilters(filters: LibraryFilters, mode: LibraryMode): LibraryFilters {
  const defaults = defaultFiltersFor(mode);
  return {
    ...filters,
    facets: {},
    flags: {},
    source: [],
    tier: [],
    // A coach's Include archived lives outside the popover.
    visibility: mode === "admin" ? defaults.visibility : filters.visibility,
  };
}

export const FLAG_WORDS: Record<LibraryFlag, string> = { hasPeople: "people", hasText: "text", hasLogo: "logos" };

const VISIBILITY_LABELS: Record<BackgroundVisibility, string> = {
  draft: "Draft",
  active: "Active",
  archived: "Archived",
};

// Values that read oddly alone ("Light", "Top") carry their facet's name in a pill.
const NAMED_FACETS: BackgroundFacet[] = ["palette", "emptySpace"];

export function facetValueLabel(facet: BackgroundFacet, value: string): string {
  return value === "none" ? "None" : facetLabel(facet, value);
}

export interface AppliedFilter {
  key: string;
  label: string;
  remove(filters: LibraryFilters): LibraryFilters;
}

/** One removable pill per applied value, in the popover's order. */
export function appliedFilters(filters: LibraryFilters, mode: LibraryMode): AppliedFilter[] {
  const pills: AppliedFilter[] = [];
  FACETS.forEach(facet => {
    (filters.facets[facet] ?? []).forEach(value => {
      const word = facetValueLabel(facet, value);
      pills.push({
        key: `${facet}:${value}`,
        label: NAMED_FACETS.includes(facet) || value === "none" ? `${FACET_TITLES[facet]}: ${word}` : word,
        remove: current => ({
          ...current,
          facets: { ...current.facets, [facet]: (current.facets[facet] ?? []).filter(v => v !== value) },
        }),
      });
    });
  });
  FLAGS.forEach(flag => {
    const value = filters.flags[flag];
    if (value === undefined) return;
    const word = FLAG_WORDS[flag];
    pills.push({
      key: flag,
      label: mode === "coach" ? `No ${word}` : `${value ? "With" : "Without"} ${word}`,
      remove: current => {
        const next = { ...current.flags };
        delete next[flag];
        return { ...current, flags: next };
      },
    });
  });
  if (mode === "admin") {
    if (!sameValues(filters.visibility, DEFAULT_LIBRARY_FILTERS.visibility)) {
      filters.visibility.forEach(value =>
        pills.push({
          key: `visibility:${value}`,
          label: `Visibility: ${VISIBILITY_LABELS[value]}`,
          remove: current => {
            const rest = current.visibility.filter(v => v !== value);
            return { ...current, visibility: rest.length > 0 ? rest : DEFAULT_LIBRARY_FILTERS.visibility };
          },
        })
      );
    }
    filters.source.forEach(value =>
      pills.push({
        key: `source:${value}`,
        label: `Source: ${SOURCE_LABELS[value]}`,
        remove: current => ({ ...current, source: current.source.filter(v => v !== value) }),
      })
    );
    filters.tier.forEach(value =>
      pills.push({
        key: `tier:${value}`,
        label: `Tier: ${TIER_LABELS[value]}`,
        remove: current => ({ ...current, tier: current.tier.filter(v => v !== value) }),
      })
    );
  }
  return pills;
}

// URL shapes for the view: short, readable, and only what differs from the defaults.
const FLAG_PARAMS: Record<LibraryFlag, string> = { hasPeople: "people", hasText: "text", hasLogo: "logo" };
const PREVIEW_PARAMS: Record<PreviewRatio, string> = {
  square_1_1: "1-1",
  portrait_4_5: "4-5",
  portrait_9_16: "9-16",
};
const VIEW_PARAMS = [
  "q",
  "mine",
  "archived",
  ...FACETS,
  ...Object.values(FLAG_PARAMS),
  "visibility",
  "source",
  "tier",
  "sort",
  "preview",
];

export interface LibraryView {
  filters: LibraryFilters;
  /** Coach mode: Only mine. */
  mine: boolean;
}

/** The view a URL describes; unknown values are dropped. */
export function readLibraryParams(search: string, mode: LibraryMode): LibraryView {
  const params = new URLSearchParams(search);
  const defaults = defaultFiltersFor(mode);
  const pick = <T extends string>(key: string, allowed: readonly T[]): T[] =>
    params.getAll(key).filter((value): value is T => (allowed as readonly string[]).includes(value));

  const facets: LibraryFilters["facets"] = {};
  FACETS.forEach(facet => {
    const allowed = (BACKGROUND_FACETS[facet] as readonly string[]).filter(v => mode === "admin" || v !== "none");
    const values = pick(facet, allowed);
    if (values.length > 0) facets[facet] = values;
  });

  const flags: LibraryFilters["flags"] = {};
  FLAGS.forEach(flag => {
    const value = params.get(FLAG_PARAMS[flag]);
    if (value === "0") flags[flag] = false;
    // Coaches only leave things out.
    if (value === "1" && mode === "admin") flags[flag] = true;
  });

  const mine = mode === "coach" && params.get("mine") === "1";
  const visibility: BackgroundVisibility[] =
    mode === "coach"
      ? mine && params.get("archived") === "1"
        ? ["active", "archived"]
        : ["active"]
      : (() => {
          const values = pick<BackgroundVisibility>("visibility", ["draft", "active", "archived"]);
          return values.length > 0 ? values : defaults.visibility;
        })();

  const previewParam = params.get("preview");
  const previewRatio =
    (Object.keys(PREVIEW_PARAMS) as PreviewRatio[]).find(ratio => PREVIEW_PARAMS[ratio] === previewParam) ??
    defaults.previewRatio;

  return {
    mine,
    filters: {
      q: params.get("q")?.trim() ?? "",
      facets,
      flags,
      visibility,
      source: mode === "admin" ? pick<BackgroundSource>("source", ["generated", "uploaded", "stock"]) : [],
      tier: mode === "admin" ? pick<BackgroundTier>("tier", ["quality", "fast"]) : [],
      sort: params.get("sort") === "popular" ? "popular" : "recent",
      previewRatio,
    },
  };
}

/** `search` with the view written over it; other params (like `asset`) are kept. */
export function writeLibraryParams(search: string, view: LibraryView, mode: LibraryMode): string {
  const params = new URLSearchParams(search);
  VIEW_PARAMS.forEach(key => params.delete(key));
  const { filters, mine } = view;

  if (filters.q) params.set("q", filters.q);
  if (mode === "coach" && mine) {
    params.set("mine", "1");
    if (filters.visibility.includes("archived")) params.set("archived", "1");
  }
  FACETS.forEach(facet => (filters.facets[facet] ?? []).forEach(value => params.append(facet, value)));
  FLAGS.forEach(flag => {
    const value = filters.flags[flag];
    if (value !== undefined) params.set(FLAG_PARAMS[flag], value ? "1" : "0");
  });
  if (mode === "admin") {
    if (!sameValues(filters.visibility, DEFAULT_LIBRARY_FILTERS.visibility))
      filters.visibility.forEach(value => params.append("visibility", value));
    filters.source.forEach(value => params.append("source", value));
    filters.tier.forEach(value => params.append("tier", value));
  }
  if (filters.sort !== "recent") params.set("sort", filters.sort);
  if (filters.previewRatio !== DEFAULT_LIBRARY_FILTERS.previewRatio)
    params.set("preview", PREVIEW_PARAMS[filters.previewRatio]);

  const next = params.toString();
  return next ? `?${next}` : "";
}
